import { z } from "zod";
export const DTOTrailEventType = z.enum([
    "IterationCreated",
    "IterationBookmarked",
    "FeatureCreated",
    "FeatureDeleted",
    "FeatureArchived",
    "DocumentCreated",
    "DocumentDeleted",
    "DocumentUpdated",
    "DocumentCommentSent",
    "ProjectCreated",
    "ProjectArchived",
    "IterationPromoted",
    "ProjectContextCreated",
    "ProjectContextArchived",
    "FeaturePublished",
    "FeatureUnpublished",
    "FeatureForked",
]);
const DTOTrailEventBase = z.object({
    id: z.string(),
    projectId: z.string(),
    userId: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const DTOTrailEventIterationCreatedPayload = z.object({
    iterationName: z.string(),
    iterationId: z.string().uuid(),
    featureId: z.string().uuid(),
});
const DTOTrailEventIterationBookmarkedPayload = z.object({
    iterationId: z.string().uuid(),
    featureId: z.string().uuid(),
    iterationName: z.string(),
});
const DTOTrailEventIterationPromotedPayload = z.object({
    iterationId: z.string().uuid(),
    featureId: z.string().uuid(),
    iterationName: z.string(),
});
const DTOTrailEventFeatureCreatedPayload = z.object({
    featureId: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
});
const DTOTrailEventFeatureDeletedPayload = z.object({
    featureId: z.string().uuid(),
    name: z.string(),
});
const DTOTrailEventFeatureArchivedPayload = z.object({
    featureId: z.string().uuid(),
    name: z.string(),
});
const DTOTrailEventDocumentCreatedPayload = z.object({
    documentId: z.string().uuid(),
    title: z.string(),
    sectionId: z.string().uuid().optional(),
});
const DTOTrailEventDocumentDeletedPayload = z.object({
    documentId: z.string().uuid(),
    title: z.string(),
});
const DTOTrailEventDocumentUpdatedPayload = z.object({
    documentId: z.string().uuid(),
    title: z.string(),
    sectionId: z.string().uuid().optional(),
});
const DTOTrailEventDocumentCommentSentPayload = z.object({
    documentId: z.string().uuid(),
    title: z.string(),
    sectionId: z.string().uuid().optional(),
});
const DTOTrailEventProjectCreatedPayload = z.object({
    name: z.string(),
    description: z.string().optional(),
});
const DTOTrailEventFeaturePublishedPayload = z.object({
    featureId: z.string().uuid(),
    featureName: z.string(),
    iterationId: z.string().uuid().nullish(),
    iterationName: z.string().nullish(),
});
const DTOTrailEventFeatureUnpublishedPayload = z.object({
    featureId: z.string().uuid(),
    featureName: z.string(),
    iterationId: z.string().uuid().nullish(),
    iterationName: z.string().nullish(),
});
const DTOTrailEventProjectArchivedPayload = z.object({
    name: z.string(),
});
const DTOTrailEventProjectContextCreatedPayload = z.object({
    contextId: z.number(),
    name: z.string(),
    description: z.string().optional(),
});
const DTOTrailEventProjectContextArchivedPayload = z.object({
    contextId: z.number(),
});
const DTOTrailEventFeatureForkedPayload = z.object({
    featureId: z.string().uuid(),
    name: z.string(),
    sourceFeatureId: z.string().uuid(),
    sourceIterationId: z.string().uuid(),
});
const DTOTrailEventPayload = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("IterationCreated"),
        payload: DTOTrailEventIterationCreatedPayload,
    }),
    z.object({
        type: z.literal("IterationBookmarked"),
        payload: DTOTrailEventIterationBookmarkedPayload,
    }),
    z.object({
        type: z.literal("FeatureCreated"),
        payload: DTOTrailEventFeatureCreatedPayload,
    }),
    z.object({
        type: z.literal("FeatureForked"),
        payload: DTOTrailEventFeatureForkedPayload,
    }),
    z.object({
        type: z.literal("FeatureDeleted"),
        payload: DTOTrailEventFeatureDeletedPayload,
    }),
    z.object({
        type: z.literal("FeatureArchived"),
        payload: DTOTrailEventFeatureArchivedPayload,
    }),
    z.object({
        type: z.literal("DocumentCreated"),
        payload: DTOTrailEventDocumentCreatedPayload,
    }),
    z.object({
        type: z.literal("DocumentDeleted"),
        payload: DTOTrailEventDocumentDeletedPayload,
    }),
    z.object({
        type: z.literal("DocumentUpdated"),
        payload: DTOTrailEventDocumentUpdatedPayload,
    }),
    z.object({
        type: z.literal("DocumentCommentSent"),
        payload: DTOTrailEventDocumentCommentSentPayload,
    }),
    z.object({
        type: z.literal("ProjectCreated"),
        payload: DTOTrailEventProjectCreatedPayload,
    }),
    z.object({
        type: z.literal("ProjectArchived"),
        payload: DTOTrailEventProjectArchivedPayload,
    }),
    z.object({
        type: z.literal("IterationPromoted"),
        payload: DTOTrailEventIterationPromotedPayload,
    }),
    z.object({
        type: z.literal("ProjectContextCreated"),
        payload: DTOTrailEventProjectContextCreatedPayload,
    }),
    z.object({
        type: z.literal("ProjectContextArchived"),
        payload: DTOTrailEventProjectContextArchivedPayload,
    }),
    z.object({
        type: z.literal("FeaturePublished"),
        payload: DTOTrailEventFeaturePublishedPayload,
    }),
    z.object({
        type: z.literal("FeatureUnpublished"),
        payload: DTOTrailEventFeatureUnpublishedPayload,
    }),
]);
export const DTOTrailEvent = DTOTrailEventPayload.and(DTOTrailEventBase);
export const DTOTrailEventWithDetails = DTOTrailEvent.and(z.object({
    projectName: z.string().optional(),
    userName: z.string().optional(),
}));
export const DTOTrailEventListInput = z.object({
    projectId: z.string(),
});
export const DTOTrailEventListResponse = z.object({
    events: z.array(DTOTrailEventWithDetails),
});
export const DTOTrailEventCreate = DTOTrailEventPayload.and(DTOTrailEventBase.omit({ id: true, createdAt: true, updatedAt: true }));
export const DTOTrailEventClientCreate = z
    .discriminatedUnion("type", [
    z.object({
        type: z.literal("DocumentUpdated"),
        payload: DTOTrailEventDocumentUpdatedPayload,
    }),
    z.object({
        type: z.literal("DocumentCommentSent"),
        payload: DTOTrailEventDocumentCommentSentPayload,
    }),
])
    .and(DTOTrailEventBase.omit({ id: true, createdAt: true, updatedAt: true }));
